<template>
    <div class="request-container">
        <!-- Provider Search -->
        <div class="section">
            <div class="tabhead">
                <div class="main">
                    <label class="label">{{ $t('modals.patient_profile.profile.request.search') }}</label>
                    <span class="verified">{{ $t('modals.patient_profile.profile.request.verify') }}</span>
                </div>
                <div class="filter-bar">
                    <common-icon class="search-icon" name="search" size="small"></common-icon>

                    <input
                        v-model="searchTerm"
                        type="text"
                        class="search-input"
                        :placeholder="$t('forms.patient_profile.fields.search.placeholder')"
                        @keyup="inputChange"
                    />
                </div>
                <common-provider-list
                    v-if="isProviderListOpen"
                    :items="items"
                    :searched="hasSearched"
                    :loading="isLoading"
                    @provider-selected="handleProviderSelect"
                ></common-provider-list>
                <div v-if="selectedProvider" class="selected-provider">
                    <div class="left">
                        <common-icon name="building" size="small" class="icon"></common-icon>
                        <div class="head-title">
                            <h4>{{ $t('modals.patient_profile.profile.request.medication.selected_provider') }}</h4>
                            <div class="title">
                                {{ selectedProvider.user.credential }} {{ selectedProvider.firstName }}
                                {{ selectedProvider.lastName }}
                            </div>

                            <p class="sub">{{ $t('modals.patient_profile.profile.request.medication.secure') }}</p>
                        </div>
                    </div>
                    <common-icon name="trash-bin" size="small" class="delete-icon" @click="removeProviderSelect"></common-icon>
                </div>
            </div>
        </div>

        <!-- Records Needed -->
        <div class="section">
            <h3 class="hipa-title">{{ $t('modals.patient_profile.profile.request.record') }}</h3>
            <div class="checkbox-group">
                <label v-for="record in recordTypeSources" :key="record.value">
                    <input v-model="selectedRecordIds" type="checkbox" :value="record.value" />
                    <p>{{ record.label }}</p>
                </label>
            </div>
        </div>
        <form-wrapper class="practice-information-form">
            <form-row>
                <form-cell width="full">
                    <label class="form-field__label">
                        {{ $t('forms.patient_profile.fields.hipaa.authorization.label') }}
                    </label>
                    <select-input
                        class="schedule-apply__input"
                        :placeholder="$t('forms.patient_profile.fields.hipaa.authorization.placeholder')"
                        :hasAutofocus="true"
                        :options="hipaaFormOptions"
                        :disabled="!hipaaFormOptions || hipaaFormOptions.length === 0"
                        :value="hipaaFormId"
                        @change="changeHipaa($event)"
                    ></select-input>
                    <p v-if="!hipaaFormOptions || hipaaFormOptions.length === 0" class="form-error-text">
                        {{ $t('modals.patient_profile.profile.request.hipaa.no_hipaa') }}
                        <span @click="openFilePicker">{{ $t('modals.patient_profile.profile.request.hipaa.upload_hipaa') }}</span>
                        <input
                            ref="hipaaFileInput"
                            type="file"
                            style="display: none"
                            accept=".pdf,.jpg,.jpeg,.png"
                            @change="onHipaaFileSelected"
                        />
                    </p>
                </form-cell>
            </form-row>
            <upload-hipaa-modal
                :isOpen="isHipaaModalOpen"
                :file="selectedHipaaFile"
                @close="closeHipaaModal"
                @refresh-hipaa="onHipaaUploaded"
            ></upload-hipaa-modal>
            <h3 class="hipa-titles">{{ $t('modals.patient_profile.profile.request.range') }}</h3>
            <form-row>
                <form-cell width="half">
                    <label class="form-field__label">
                        {{ $t('forms.patient_profile.fields.date_range.label') }}
                    </label>
                    <text-field
                        class=""
                        :placeholder="$t('forms.patient_profile.fields.date_range.insurance_plans.placeholder')"
                        :isRequired="false"
                        :maxDate="maxSelectableDate"
                        :formData="data"
                        valuePath="dateFrom"
                        type="date"
                        @change="change($event)"
                    ></text-field>
                </form-cell>
                <form-cell width="half">
                    <label class="form-field__label">
                        {{ $t('forms.patient_profile.fields.date_range.document.label') }}
                    </label>
                    <text-field
                        class=""
                        :placeholder="$t('forms.patient_profile.fields.date_range.document.placeholder')"
                        :isRequired="false"
                        :maxDate="maxSelectableDate"
                        :formData="data"
                        valuePath="dateTo"
                        type="date"
                        @change="change($event)"
                    ></text-field>
                </form-cell>
            </form-row>
            <form-row>
                <form-cell width="full">
                    <label class="form-field__label">
                        {{ $t('forms.patient_profile.fields.date_range.provider.label') }}
                    </label>
                    <select-input
                        class="schedule-apply__input"
                        :placeholder="$t('forms.patient_profile.fields.date_range.provider.placeholder')"
                        :hasAutofocus="true"
                        :options="requestPurposeOption"
                        :value="requestPurposeId"
                        @change="changeRequestPurpose($event)"
                    ></select-input>
                </form-cell>
            </form-row>
            <form-row>
                <form-cell width="full">
                    <textarea-field
                        :label="$t('forms.patient_profile.fields.profile.additional.label')"
                        :placeholder="$t('forms.patient_profile.fields.profile.additional.placeholder')"
                        :isRequired="true"
                        :formData="data"
                        valuePath="notes"
                        @change="change($event)"
                    ></textarea-field>
                </form-cell>
            </form-row>
        </form-wrapper>
        <div class="submit">
            <button :class="requestButtonClass" :disabled="!isFormValid" @click="submitRecordRequest">
                <common-icon name="send" size="small"></common-icon>
                <span>{{ $t('forms.patient_profile.fields.date_range.button.label') }}</span>
            </button>
        </div>
        <common-notification ref="commonSuccessNotificationChild" :content="notificationMessage"></common-notification>
    </div>
</template>
<script>
    import FormMixin from 'mixins/FormMixin'
    import HandleRequestMixin from 'mixins/HandleRequestMixin'
    import debounce from 'lodash.debounce'
    import config from '../../../config'
    import PatientRecordsAPI from 'api/PatientRecordsAPI'
    import ProviderDatabaseAPI from 'api/ProviderDatabaseAPI'
    import dayjs from 'dayjs'
    import { dateFormate } from '~/helpers'
    import UploadHipaaModal from '../hippa/UploadHipaaModal.vue'

    export default {
        mixins: [FormMixin, HandleRequestMixin],
        components: {
            UploadHipaaModal,
        },

        props: {
            availHipaaForms: { type: Array, required: true },
            recordTypeSources: { type: Array, required: true },
        },

        data() {
            return {
                requestPurposeOption: [
                    { label: 'Continuing Care', value: 1 },
                    { label: 'Specialist Referral', value: 2 },
                    { label: 'Second Opinion', value: 3 },
                    { label: 'Personal Records', value: 4 },
                    { label: 'Insurance Claim', value: 5 },
                    { label: 'Legal Proceeding', value: 6 },
                    { label: 'Other', value: 7 },
                ],
                hipaaFormId: null,
                requestPurposeId: null,
                selectedRecordIds: [],
                items: [],
                searchTerm: '',
                selectedProvider: null,
                hasSearched: false,
                isLoading: false,
                notificationMessage: '',
                isProviderListOpen: false,
                isHipaaModalOpen: false,
                selectedHipaaFile: null,
            }
        },
        computed: {
            requestButtonClass() {
                return {
                    request: true,
                    'request--disabled': !this.isFormValid,
                }
            },
            isFormValid() {
                return Boolean(
                    this.selectedProvider &&
                        this.selectedRecordIds?.length > 0 &&
                        this.hipaaFormId &&
                        this.requestPurposeId &&
                        this.data.notes?.trim(),
                )
            },
            hipaaFormOptions() {
                const today = dayjs()

                return (this.availHipaaForms || [])
                    .filter((form) => !dayjs(form.expiryDate).isBefore(today, 'day'))
                    .map((form) => ({
                        label: `${form.authorizationName} - ${form.scope}  (expires ${dateFormate(form.expiryDate)})`,
                        value: form.id,
                    }))
            },
            selectedRecordTypes() {
                return this.recordTypeSources
                    .filter((record) => this.selectedRecordIds.includes(record.value))
                    .map((record) => record.label)
            },
            selectedPurpose() {
                return this.requestPurposeOption.find((item) => item.value === this.requestPurposeId)
            },
            maxSelectableDate() {
                return dayjs().format('YYYY-MM-DD')
            },
        },
        methods: {
            dateFormate,
            inputChange: debounce(function () {
                this.isProviderListOpen = true
                this.emitEvent({ name: 'input-change' })
                if (this.searchTerm.length < 2) {
                    this.items = []
                    this.hasSearched = false
                    this.isLoading = false
                    return
                }

                this.hasSearched = true
                this.isLoading = true

                ProviderDatabaseAPI.search({
                    query: this.searchTerm,
                })
                    .then((val) => {
                        this.items = Array.isArray(val) ? val : []
                    })
                    .catch(() => {
                        this.items = []
                    })
                    .finally(() => {
                        this.isLoading = false
                    })
            }, config.DEBOUNCE.DEFAULT),

            handleProviderSelect(provider) {
                this.selectedProvider = provider
                this.isProviderListOpen = false
            },
            removeProviderSelect() {
                this.selectedProvider = null
            },
            changeHipaa(event) {
                this.hipaaFormId = event
            },
            changeRequestPurpose(event) {
                this.requestPurposeId = event
            },

            submitRecordRequest() {
                if (!this.isFormValid) {
                    return
                }
                this.notificationMessage = ''
                const payload = {
                    patientId: Number(this.$route.params.id),
                    npiNumber: this.selectedProvider.npi,
                    fromProviderId: this.$auth.getUser().id,
                    dateFrom: this.data.dateFrom,
                    dateTo: this.data.dateTo,
                    purpose: this.selectedPurpose?.label,
                    recordTypes: this.selectedRecordTypes,
                    notes: this.data.notes,
                    hipaaFormId: this.hipaaFormId,
                }
                PatientRecordsAPI.create(payload).then(() => {
                    this.notificationMessage = this.$t('modals.patient_profile.profile.request.notification')
                    this.$refs.commonSuccessNotificationChild.showNotification()
                    this.resetRecordRequestForm()
                    this.emitEvent({ name: 'refresh-record-request' })
                })
            },
            resetRecordRequestForm() {
                this.data.dateFrom = null
                this.data.dateTo = null
                this.data.notes = ''

                this.hipaaFormId = null
                this.requestPurposeId = null
                this.selectedRecordIds = []

                this.selectedProvider = null
                this.items = []
                this.searchTerm = ''
                this.hasSearched = false
            },
            openFilePicker() {
                this.$refs.hipaaFileInput.click()
            },
            onHipaaFileSelected(event) {
                const file = event.target.files[0]
                if (!file) return

                this.selectedHipaaFile = file
                this.isHipaaModalOpen = true
                if (this.$refs.hipaaFileInput) {
                    this.$refs.hipaaFileInput.value = ''
                }
            },
            closeHipaaModal() {
                this.isHipaaModalOpen = false
                this.selectedHipaaFile = null
            },
            onHipaaUploaded() {
                this.emitEvent({ name: 'get-hipaa-forms' })
            },
            getBase64(file) {
                return new Promise((resolve, reject) => {
                    const reader = new FileReader()
                    reader.readAsDataURL(file)
                    reader.onload = () => resolve(reader.result)
                    reader.onerror = (error) => reject(error)
                })
            },
        },

        watch: {
            hipaaFormOptions: {
                immediate: true,
                handler(options) {
                    if (!this.hipaaFormId && options && options.length > 0) {
                        this.hipaaFormId = options[0].value
                    }
                },
            },
        },
    }
</script>
<style scoped lang="scss">
    @import 'styles/setup/_variables.scss';
    @import 'styles/setup/_mixins.scss';
    .submit {
        margin-top: 20px;
    }

    .request {
        background-color: #155dfc;
        border: none;
        border-radius: 8px;
        color: #fff;
        padding: 8px 12px;
        margin: 10px 0;

        display: inline-flex;
        align-items: center;
        gap: 14px;

        cursor: pointer;
        transition: opacity 0.2s ease, background-color 0.2s ease, transform 0.2s ease;
    }

    .request :deep(svg),
    .request :deep(i) {
        color: #fff;
        fill: #fff;
    }

    .request:hover:not(:disabled) {
        transform: translateY(-1px);
    }

    .request--disabled,
    .request:disabled {
        opacity: 0.45;
        cursor: not-allowed;
    }
    .tabhead {
        padding-bottom: 25px;
        border-bottom: 1px solid #ddd;
    }

    .filter-bar {
        position: relative;
        width: 100%;
        display: flex;
        align-items: center;
        i {
            color: #99a1af;
        }
    }

    .search-input {
        width: 100%;
        height: 40px;
        padding: 8px 16px 8px 40px;
        border: 1px solid #f5f5f5;
        border-radius: 10px;
        background: #f5f5f5;
        font-size: 12px;
        outline: none;

        &:focus {
            border-color: #d9d9d9;
            background: #fff;
        }
    }

    .search-icon {
        position: absolute;
        left: 14px;
        top: 50%;
        transform: translateY(-50%);
        pointer-events: none;
        z-index: 1;
    }

    .hipa-title {
        font-size: 12px;
        font-weight: 500;
        color: $color-dark-2;
        margin: 10px 0px;
    }

    .hipa-titles {
        font-size: 12px;
        font-weight: 500;
        color: $color-dark-2;
        margin: 18px 0px 0px;
    }

    .section {
        margin-bottom: 20px;
    }

    .label {
        font-weight: 500;
        display: block;
        margin-bottom: 8px;
        color: $color-dark-2;
        font-size: 16px;
    }

    .checkbox-group {
        display: grid;
        grid-template-columns: repeat(4, 1fr);
        gap: 8px 20px;
        font-size: 14px;
        align-items: center;
    }

    .checkbox-group label {
        display: flex;
    }

    .checkbox-group label p {
        margin-left: 10px;
        color: $color-dark-2;
        font-size: 13px;
        font-weight: 500;
    }

    .main {
        display: flex;
        align-items: center;
        justify-content: space-between;
        width: 100%;
        margin-bottom: 12px;
    }

    .verified {
        display: inline-block;
        background-color: #dcfce7;
        color: #016630;
        font-size: 10.5px;
        padding: 1.75px 7px;
        border-radius: 6.75px;
        margin-top: 6px;
        font-weight: 500;
        gap: 3.5px;
    }

    @media screen and (max-width: 600px) {
        .checkbox-group {
            grid-template-columns: repeat(2, 1fr);
        }

        .label {
            width: 158px;
        }
    }
    .selected-provider {
        background: #fff;
        border: 1px solid #e5e7eb;
        border-radius: 8px;
        margin-top: 20px;
        width: 100%;
        padding: 14px 12px;
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        gap: 8px;
    }
    .icon {
        color: black;
    }
    .head-title {
        color: #717182;
        font-size: 13px;
    }
    .head-title h4 {
        font-size: 14px;
        font-weight: bolder;
        margin-bottom: 3px;
    }
    .head-title .title {
        line-height: 1.2;
    }
    .form-error-text {
        color: #e7000b;
        font-size: 12px;
        margin-top: 6px;

        span {
            color: #e7000b;
            font-weight: 500;
            text-decoration: underline;
            cursor: pointer;
            transition: color 0.2s ease;

            &:hover {
                color: #991b1b;
            }
        }
    }
    .selected-provider .left {
        display: flex;
        align-items: flex-start;
        gap: 10px;
    }
    .delete-icon {
        cursor: pointer;
        color: red;
    }
</style>
